export const MAX_PLAYERS_PER_CLUB = 3;

type ClubbedPlayer = { team: { shortName: string } };

type SquadPlayer = ClubbedPlayer & { id: string };

type StagedTransfer = {
  outgoing: { id: string };
  incoming: ClubbedPlayer | null;
};

export type ClubOverflow = { teamShortName: string; count: number };

/**
 * Returns the first club in the squad that exceeds the FPL per-club limit, or
 * null when every club is within it. Lets the UI name the offending club while
 * the squad is being built, rather than leaving the user with the backend's
 * generic squad rejection.
 */
export const findClubOverflowInSquad = (
  squad: ClubbedPlayer[]
): ClubOverflow | null => {
  const countsByClub = new Map<string, number>();
  for (const player of squad) {
    const { shortName } = player.team;
    countsByClub.set(shortName, (countsByClub.get(shortName) ?? 0) + 1);
  }

  for (const [teamShortName, count] of countsByClub) {
    if (count > MAX_PLAYERS_PER_CLUB) return { teamShortName, count };
  }
  return null;
};

/**
 * Projects the squad after the staged transfers are applied and returns the
 * first club that would exceed the FPL per-club limit, or null if none would.
 * Lets the UI warn before committing rather than relying on the backend's
 * CLUB_LIMIT_EXCEEDED rejection. Mirrors scoreline-app's findClubOverflow.
 */
export const findClubOverflow = (
  squad: SquadPlayer[],
  stagedTransfers: StagedTransfer[]
): ClubOverflow | null => {
  const incomingByOutgoingId = new Map(
    stagedTransfers
      .filter(
        (transfer): transfer is StagedTransfer & { incoming: ClubbedPlayer } =>
          transfer.incoming !== null
      )
      .map((transfer) => [transfer.outgoing.id, transfer.incoming])
  );

  const projectedSquad = squad.map(
    (player) => incomingByOutgoingId.get(player.id) ?? player
  );

  return findClubOverflowInSquad(projectedSquad);
};
